import { useRouter } from 'next/router';
import { Fragment, useEffect } from 'react';
import styled from 'styled-components';
import { mqFrom } from 'styles/styles';
import i18n, { SettingsType } from 'utils/i18n';
import RichText from '@madebyconnor/rich-text-to-jsx';
import { Controller, useWatch } from 'react-hook-form';
import ImageWithZoom from 'components/image-with-zoom';
import { isValidString } from 'utils/helpers';

type MultiQuestionProps = {
  answers: any[];
  answersCurrentLocale: any[];
  question: any;
  completed?: boolean;
  text?: string;
  questionIndex: number;
  control: any;
  image?: any;
  name?: string;
  onAnswerChange?: any;
  questionId?: string;
  questionPoolId?: string;
  globalQuestionNumber?: number;
};

const MultiQuestion: React.FC<MultiQuestionProps> = ({
  answers,
  answersCurrentLocale,
  completed,
  question,
  questionIndex,
  control,
  image,
  name = `questions.${questionIndex}.answers`,
  onAnswerChange = null,
  questionId,
  questionPoolId,
  globalQuestionNumber = null,
}) => {
  const { locale } = useRouter();
  const { dictionary } = i18n[(locale as AcceptedLocales) || 'en-US'] as SettingsType;

  const selectedAnswers = useWatch({
    control,
    name: name,
    defaultValue: [],
  });

  const correctAnswersIndices = answers.reduce((acc, answer, index) => {
    if (answer.correctCheck) {
      acc.push(index);
    }
    return acc;
  }, []);

  const allRequiredAnswersSelected =
    (selectedAnswers.length === correctAnswersIndices.length &&
      selectedAnswers.every((index) => answers[index].correctCheck)) ||
    false;

  useEffect(() => {
    if (questionId && onAnswerChange) {
      onAnswerChange({
        'content-question-id': questionId,
        'content-pool-id': questionPoolId,
        'content-answer-id': selectedAnswers.map((ans, index) => answers[index]?.contentId).join(','),
        correct: allRequiredAnswersSelected,
        name: name,
        answers: answers,
        selectedAnswers: selectedAnswers,
      });
    }
  }, [selectedAnswers]);

  const handleCheckboxChange = (answerIndex, checked) => {
    let updatedAnswers = [...selectedAnswers];
    if (checked) {
      updatedAnswers.push(answerIndex);
    } else {
      updatedAnswers = updatedAnswers.filter((index) => index !== answerIndex);
    }
    return updatedAnswers;
  };

  return (
    <StyledMultiQuestion>
      <div className="label">
        {!!globalQuestionNumber && <span className="number">{globalQuestionNumber}.&nbsp;</span>}
        {!globalQuestionNumber && <span className="number">{questionIndex + 1}.&nbsp;</span>}
        <div className="text-content">
          <RichText richText={question} />
        </div>
      </div>
      <div className="answers">
        {answers.map((answer, answerIndex) => {
          const isSelected = selectedAnswers.includes(answerIndex);
          const showResult = isSelected && completed;

          const isCorrect = completed && answer.correctCheck;
          const isWrong = showResult && !answer.correctCheck;
          const showRightAnswer = completed && answer.correctCheck;

          const currentLocaleAnswer = answersCurrentLocale?.find(
            (answerLocale) => answerLocale.contentId === answer.contentId
          );

          return (
            <Fragment key={answer.contentId}>
              <div
                className={`answer ${isSelected ? 'selected' : ''} 
                ${completed ? 'no-hover' : ''}
                ${isCorrect || showRightAnswer ? 'is-correct' : ''} 
                ${isWrong ? 'is-wrong' : ''}`}
              >
                <label>
                  <Controller
                    control={control}
                    name={name}
                    rules={{ required: 'This field is required' }}
                    render={({ field }) => (
                      <input
                        type="checkbox"
                        {...field}
                        checked={isSelected}
                        onChange={(e) => field.onChange(handleCheckboxChange(answerIndex, e.target.checked))}
                        value={answerIndex}
                        aria-checked={isSelected ? 'true' : 'false'}
                        className="multi"
                      />
                    )}
                  />
                  {locale !== 'en-US' && isValidString(currentLocaleAnswer.text)
                    ? currentLocaleAnswer.text
                    : answer.text}
                </label>
              </div>

              {(showResult || showRightAnswer) && (
                <div className={`message ${showRightAnswer ? 'is-correct' : ''} ${isWrong ? 'is-wrong' : ''}`}>
                  {showRightAnswer && <span className="checkmark-completed docon docon-check" />}
                  {isWrong && <span className="checkmark-completed docon docon-math-multiply" />}
                  <span className="answer-message">
                    {locale !== 'en-US' && isValidString(currentLocaleAnswer.message)
                      ? currentLocaleAnswer.message
                      : answer.message}
                  </span>
                </div>
              )}
            </Fragment>
          );
        })}
      </div>
      {image && (
        <div className="image-wrapper">
          <ImageWithZoom src={image.url} alt={image.title} width={image.width} height={image.height} />
        </div>
      )}
    </StyledMultiQuestion>
  );
};

export const StyledMultiQuestion = styled.ul`
  margin: 1.5rem 0 1rem 0;

  .label {
    display: inline-flex;

    .number {
      font-weight: 600;
      margin-right: 0.5rem;
    }
  }

  label {
    display: flex;
    align-items: center;
    width: 100%;
    cursor: pointer;
  }

  .text-content {
    p + p {
      margin-top: 0.5rem;
    }
  }

  .hidden-radio {
    opacity: 0;
    position: absolute;
    left: -9999px;
  }

  .answers {
    margin: 0.5rem 0;
    ${mqFrom.tablet} {
      margin: 1rem 3rem 0 3rem;
    }
  }

  .answer {
    padding: 0.5rem 1rem;
    margin-bottom: 0.5rem;
    position: relative;
    display: flex;
    align-items: center;
    background-color: var(--body-background-medium);
    border: 1px solid transparent;
    border-radius: 0.25rem;
    cursor: pointer;

    ${mqFrom.tablet} {
      background-color: transparent;
      border-radius: 1rem;
      padding: 0 1rem;
    }

    &:hover {
      background-color: transparent;
      border-color: transparent;
      box-shadow: 0 0 5px 0 var(--box-shadow-light);
    }

    .multi {
      margin-right: 1rem;
      background: var(--primary-base);
    }

    > .label {
      flex: 1 1 0;
    }

    .message {
      font-weight: 600;
      margin: 0.25rem 0 0.5rem 3rem;
      padding-left: 1rem;
      display: flex;
      align-items: baseline;
    }

    &.selected {
      background-color: var(--primary-background);
      border-color: var(--primary-base);

      input[type='checkbox'] {
        accent-color: var(--primary-base);
      }
    }

    &.is-correct {
      background-color: var(--success-background);
      border-color: var(--success-base);
      margin-bottom: 0;

      label {
        opacity: 0.5;
      }

      .message {
        color: var(--success-base);
      }
    }

    &.is-wrong {
      background-color: var(--danger-background);
      border-color: var(--danger-base);
      margin-bottom: 0;

      label {
        opacity: 0.5;
      }

      .message {
        color: var(--danger-base);
      }
    }
  }

  .message {
    font-weight: 600;
    margin: 0.25rem 0 0.5rem 3rem;
    padding-left: 1rem;

    &.is-correct {
      color: var(--success-base);
    }

    &.is-wrong {
      color: var(--danger-base);
    }
  }

  .answer-message {
    margin-left: 0.5rem;
  }

  .image-wrapper {
    margin: 1.5rem 0 0 0;
    padding-left: 1.5rem;
    width: 100%;
  }
`;

export default MultiQuestion;
